/**
 * Contact reducer for managing an indexed contact store
 */

import type { Contact, ContactList } from './types';
import { ContactLogic } from './logic';

export type DuplicateContact = {
  readonly contact: Contact;
  readonly source: 'incoming' | 'existing';
  readonly addToRecipients?: boolean;
};

export type DuplicateContactGroup = readonly DuplicateContact[];

export interface ContactIndex {
  byId: Record<string, Contact>;
  allIds: string[];
  recipientIds: string[];
  duplicatesToResolve: DuplicateContactGroup[];
}

/** The part of the index that makes up the address book itself. */
export type BookContents = Pick<ContactIndex, 'allIds' | 'byId'>;

/**
 * Whether two reads of the index hold the same address book. Every reducer case
 * that changes the book replaces allIds or byId, so reference equality is exact.
 */
export function isSameBook(a: BookContents, b: BookContents): boolean {
  return a.allIds === b.allIds && a.byId === b.byId;
}

export type ContactAction =
  | {
      type: 'ADD_CONTACT';
      payload: { fields: Partial<Omit<Contact, 'localId' | 'uid'>> };
    }
  | { type: 'REMOVE_CONTACT'; payload: { localId: string } }
  | {
      type: 'UPDATE_CONTACT';
      payload: { localId: string; fields: Partial<Contact> };
    }
  | { type: 'PROVISIONALLY_ADD_CONTACTS'; payload: ContactList }
  | { type: 'PROVISIONALLY_ADD_RECIPIENTS'; payload: ContactList }
  | { type: 'SET_CONTACTS'; payload: ContactList }
  | {
      // SET_CONTACTS for a background replace that was decided against an
      // earlier read of the book: it applies only while the book is still the
      // one in `basis`, so it can never overwrite an edit made in the meantime.
      type: 'REPLACE_CONTACTS_IF_UNCHANGED';
      payload: { contacts: ContactList; basis: BookContents };
    }
  | { type: 'ADD_CONTACTS'; payload: ContactList }
  | { type: 'RESTORE_DUPLICATES'; payload: DuplicateContactGroup[] }
  | {
      type: 'RESOLVE_DUPLICATE';
      payload: { resolution: 'merge' | 'dismiss'; index?: number };
    }
  | { type: 'ADD_RECIPIENT'; payload: Contact }
  | { type: 'ADD_RECIPIENTS'; payload: ContactList }
  | { type: 'REMOVE_RECIPIENT'; payload: { localId: string } }
  | { type: 'SET_RECIPIENTS'; payload: ContactList }
  | { type: 'CLEAR_RECIPIENTS' }
  | {
      type: 'ENRICH_CONTACTS';
      payload: ReadonlyArray<{
        localId: string;
        snowballId?: string; // omit to leave existing value unchanged
        lastSnowballRefreshDate: string;
      }>;
    };

// --- Helpers ---

function addContactToById(
  byId: Record<string, Contact>,
  contact: Contact
): Record<string, Contact> {
  return { ...byId, [contact.localId]: contact };
}

function addManyToById(
  byId: Record<string, Contact>,
  contacts: ContactList
): Record<string, Contact> {
  if (contacts.length === 0) return byId;
  const result = { ...byId };
  for (const contact of contacts) {
    result[contact.localId] = contact;
  }
  return result;
}

function replaceContacts(
  state: ContactIndex,
  contacts: ContactList
): ContactIndex {
  const byId: Record<string, Contact> = {};
  const allIds: string[] = [];
  for (const contact of contacts) {
    byId[contact.localId] = contact;
    allIds.push(contact.localId);
  }
  return {
    ...state,
    byId,
    allIds,
    recipientIds: [],
    duplicatesToResolve: [],
  };
}

function removeFromById(
  byId: Record<string, Contact>,
  id: string
): Record<string, Contact> {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { [id]: _, ...rest } = byId;
  return rest;
}

// --- Selectors ---

export function selectAllContacts(state: ContactIndex): Contact[] {
  return state.allIds.map(id => state.byId[id]).filter(Boolean);
}

export function selectRecipients(state: ContactIndex): Contact[] {
  return state.recipientIds.map(id => state.byId[id]).filter(Boolean);
}

export function selectContact(
  state: ContactIndex,
  id: string
): Contact | undefined {
  return state.byId[id];
}

/**
 * Returns a sorted, deduplicated list of all listing category names across all contacts.
 */
export function selectAllListingCategories(state: ContactIndex): string[] {
  const categorySet = new Set<string>();
  for (const id of state.allIds) {
    const contact = state.byId[id];
    if (contact) {
      for (const cat of contact.listingCategories) {
        if (cat.trim()) categorySet.add(cat.trim());
      }
    }
  }
  return Array.from(categorySet).sort((a, b) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' })
  );
}

/**
 * Returns all contacts that belong to the given listing category.
 */
export function selectContactsByListingCategory(
  state: ContactIndex,
  listingCategory: string
): Contact[] {
  return state.allIds
    .map(id => state.byId[id])
    .filter(
      (contact): contact is Contact =>
        Boolean(contact) &&
        contact.listingCategories.some(
          cat =>
            cat.trim().toLowerCase() === listingCategory.trim().toLowerCase()
        )
    );
}

// --- Initial state factory ---

export function createEmptyIndex(): ContactIndex {
  return {
    byId: {},
    allIds: [],
    recipientIds: [],
    duplicatesToResolve: [],
  };
}

/**
 * Exhaustive type checking helper
 */
function assertNever(value: never): never {
  throw new Error(`Unexpected action: ${JSON.stringify(value)}`);
}

/**
 * Reducer for ContactIndex management
 */
export function contactReducer(
  state: ContactIndex,
  action: ContactAction
): ContactIndex {
  switch (action.type) {
    case 'ADD_CONTACT': {
      const newContact = ContactLogic.create(action.payload.fields);
      return {
        ...state,
        byId: addContactToById(state.byId, newContact),
        allIds: [newContact.localId, ...state.allIds],
      };
    }

    case 'REMOVE_CONTACT': {
      const { localId } = action.payload;
      return {
        ...state,
        byId: removeFromById(state.byId, localId),
        allIds: state.allIds.filter(id => id !== localId),
        recipientIds: state.recipientIds.filter(id => id !== localId),
      };
    }

    case 'UPDATE_CONTACT': {
      const existing = state.byId[action.payload.localId];
      if (!existing) return state;
      return {
        ...state,
        byId: {
          ...state.byId,
          [action.payload.localId]: {
            ...existing,
            ...action.payload.fields,
          },
        },
      };
    }

    case 'SET_CONTACTS':
      return replaceContacts(state, action.payload);

    case 'REPLACE_CONTACTS_IF_UNCHANGED':
      return isSameBook(state, action.payload.basis)
        ? replaceContacts(state, action.payload.contacts)
        : state;

    case 'RESTORE_DUPLICATES':
      return { ...state, duplicatesToResolve: action.payload };

    case 'ADD_CONTACTS': {
      const existingIds = new Set(state.allIds);
      const newContactIds = action.payload
        .map(c => c.localId)
        .filter(id => {
          if (existingIds.has(id)) return false;
          existingIds.add(id);
          return true;
        });

      return {
        ...state,
        byId: addManyToById(state.byId, action.payload),
        allIds: [...state.allIds, ...newContactIds],
        duplicatesToResolve: [],
      };
    }

    case 'PROVISIONALLY_ADD_CONTACTS': {
      const existingContacts = selectAllContacts(state);
      const duplicateGroups = ContactLogic.findDuplicatesByName(
        existingContacts,
        action.payload
      );
      const duplicateIncomingIds = new Set(
        duplicateGroups.flatMap(group =>
          ContactLogic.getIncoming(group).map(c => c.localId)
        )
      );

      const nonDuplicates = action.payload.filter(
        c => !state.byId[c.localId] && !duplicateIncomingIds.has(c.localId)
      );

      return {
        ...state,
        byId: addManyToById(state.byId, nonDuplicates),
        allIds: [...state.allIds, ...nonDuplicates.map(c => c.localId)],
        duplicatesToResolve: [...state.duplicatesToResolve, ...duplicateGroups],
      };
    }

    case 'PROVISIONALLY_ADD_RECIPIENTS': {
      const existingContacts = selectAllContacts(state);
      const duplicateGroups = ContactLogic.findDuplicatesByName(
        existingContacts,
        action.payload
      );
      const duplicateIncomingIds = new Set(
        duplicateGroups.flatMap(group =>
          ContactLogic.getIncoming(group).map(c => c.localId)
        )
      );

      const nonDuplicates = action.payload.filter(
        c => !state.byId[c.localId] && !duplicateIncomingIds.has(c.localId)
      );

      // Mark duplicate groups so resolved contacts land in recipientIds
      const recipientDuplicateGroups: DuplicateContactGroup[] =
        duplicateGroups.map(
          group =>
            group.map(dup => ({
              ...dup,
              addToRecipients: true,
            })) as unknown as DuplicateContactGroup
        );

      return {
        ...state,
        byId: addManyToById(state.byId, nonDuplicates),
        allIds: [...state.allIds, ...nonDuplicates.map(c => c.localId)],
        recipientIds: [
          ...state.recipientIds,
          ...nonDuplicates.map(c => c.localId),
        ],
        duplicatesToResolve: [
          ...state.duplicatesToResolve,
          ...recipientDuplicateGroups,
        ],
      };
    }

    case 'RESOLVE_DUPLICATE': {
      const { resolution, index } = action.payload;
      const targetIndex = index !== undefined ? index : 0;

      if (targetIndex < 0 || targetIndex >= state.duplicatesToResolve.length) {
        return state;
      }

      const group = state.duplicatesToResolve[targetIndex];
      const remainingGroups = state.duplicatesToResolve.filter(
        (_, i) => i !== targetIndex
      );

      if (!group) return state;

      const incomingContacts = ContactLogic.getIncoming(group);
      const existingContacts = ContactLogic.getExisting(group);
      const addToRecipients = group.some(d => d.addToRecipients);

      if (resolution === 'merge') {
        const merged = ContactLogic.mergeContacts(group);

        if (existingContacts.length > 0) {
          // Update existing contact with merged data
          const existingLocalId = existingContacts[0].localId;
          return {
            ...state,
            duplicatesToResolve: remainingGroups,
            byId: {
              ...state.byId,
              [existingLocalId]: merged,
            },
            recipientIds:
              addToRecipients && !state.recipientIds.includes(existingLocalId)
                ? [...state.recipientIds, existingLocalId]
                : state.recipientIds,
          };
        } else {
          // Add new merged contact
          return {
            ...state,
            duplicatesToResolve: remainingGroups,
            byId: addContactToById(state.byId, merged),
            allIds: [...state.allIds, merged.localId],
            recipientIds: addToRecipients
              ? [...state.recipientIds, merged.localId]
              : state.recipientIds,
          };
        }
      } else {
        // dismiss: add all incoming contacts individually
        return {
          ...state,
          duplicatesToResolve: remainingGroups,
          byId: addManyToById(state.byId, incomingContacts),
          allIds: [...state.allIds, ...incomingContacts.map(c => c.localId)],
          recipientIds: addToRecipients
            ? [...state.recipientIds, ...incomingContacts.map(c => c.localId)]
            : state.recipientIds,
        };
      }
    }

    case 'ADD_RECIPIENT': {
      const contact = action.payload;
      // Add to byId if not already present
      const byId = state.byId[contact.localId]
        ? state.byId
        : addContactToById(state.byId, contact);
      // Add to allIds if not already present
      const allIds = state.allIds.includes(contact.localId)
        ? state.allIds
        : [...state.allIds, contact.localId];
      return {
        ...state,
        byId,
        allIds,
        recipientIds: [...state.recipientIds, contact.localId],
      };
    }

    case 'ADD_RECIPIENTS': {
      const contacts = action.payload;
      let byId = state.byId;
      let allIds = state.allIds;
      const newRecipientIds: string[] = [];

      for (const contact of contacts) {
        if (!byId[contact.localId]) {
          byId = addContactToById(byId, contact);
        }
        if (!allIds.includes(contact.localId)) {
          allIds = [...allIds, contact.localId];
        }
        newRecipientIds.push(contact.localId);
      }

      return {
        ...state,
        byId,
        allIds,
        recipientIds: [...state.recipientIds, ...newRecipientIds],
      };
    }

    case 'REMOVE_RECIPIENT': {
      const { localId } = action.payload;
      return {
        ...state,
        recipientIds: state.recipientIds.filter(id => id !== localId),
      };
    }

    case 'SET_RECIPIENTS': {
      const contacts = action.payload;
      let byId = state.byId;
      let allIds = state.allIds;

      for (const contact of contacts) {
        if (!byId[contact.localId]) {
          byId = addContactToById(byId, contact);
        }
        if (!allIds.includes(contact.localId)) {
          allIds = [...allIds, contact.localId];
        }
      }

      return {
        ...state,
        byId,
        allIds,
        recipientIds: contacts.map(c => c.localId),
      };
    }

    case 'CLEAR_RECIPIENTS':
      return {
        ...state,
        recipientIds: [],
      };

    case 'ENRICH_CONTACTS': {
      if (action.payload.length === 0) return state;
      const byId = { ...state.byId };
      for (const update of action.payload) {
        const existing = byId[update.localId];
        if (!existing) continue;
        byId[update.localId] = {
          ...existing,
          lastSnowballRefreshDate: update.lastSnowballRefreshDate,
          ...(update.snowballId !== undefined && {
            snowballId: update.snowballId,
          }),
        };
      }
      return { ...state, byId };
    }

    default:
      return assertNever(action);
  }
}
